import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import StatusBadge from '../account/components/StatusBadge';
import MetricsCards from './components/MetricsCards';
import PlanBreakdownChart from './components/PlanBreakdownChart';
import SubscriptionDetailModal from './components/SubscriptionDetailModal';
import AlertService from '../../utils/alertService';
import {
  Badge, Button, Card, EmptyState, IconButton, SearchInput, Select,
  SkeletonKpis, SkeletonRows,
} from '../../components/app2';
import billingService, {
  describeStatus,
  formatMxn,
  formatDateLong,
} from '../../utils/billingService';

/**
 * Suscripciones de toda la plataforma (rediseño jul-2026).
 *
 * Mismos datos, filtros, paginación y detalle que siempre; lo que cambia es el
 * lenguaje visual: tarjetas-KPI y tabla del sistema, tarjetas en móvil (antes
 * la tabla se iba de ancho) y el estado de Stripe como etiqueta en vez de una
 * línea de texto con el identificador de la cuenta a la vista (ese id sigue
 * disponible en el tooltip de la etiqueta: es información de diagnóstico, no
 * algo que deba leerse en cada visita).
 *
 * El título ya lo pinta el header del shell, así que la pantalla ya no repite
 * su propio <h1>.
 */

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'active', label: 'Activas' },
  { value: 'trialing', label: 'En prueba' },
  { value: 'past_due', label: 'Pago pendiente' },
  { value: 'canceled', label: 'Canceladas' },
  { value: 'unpaid', label: 'No pagadas' },
  { value: 'grace_period', label: 'Periodo de gracia' },
  { value: 'no_subscription', label: 'Sin plan' },
];

const PAGE_SIZE = 25;

/* Las siete columnas deben caber SIN scroll en un escritorio de 1280px: el menú
   lateral (250) y los paddings dejan ~964 útiles. El reparto es 808 (columnas)
   + 60 (6 separaciones de 10) + 40 (padding de la fila) = 908.
   Aun así el contenedor conserva overflow-x por si la ventana es más angosta, y
   por debajo de 1024 se cambia a tarjetas. */
const GRID = 'minmax(190px,1.4fr) 140px 126px 92px 128px 84px 48px';
const GRID_GAP = 10;
const GRID_MIN = 908;

const AdminSubscriptions = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [plans, setPlans] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [syncingPlans, setSyncingPlans] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  useEffect(() => {
    loadPlans();
    loadMetrics();
    loadStripeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, planFilter, searchDebounced]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const loadSubscriptions = async () => {
    try {
      setLoading(true);
      const res = await billingService.admin.listSubscriptions({
        page,
        pageSize: PAGE_SIZE,
        status: statusFilter || undefined,
        planId: planFilter || undefined,
        search: searchDebounced || undefined,
      });
      setSubscriptions(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.meta?.pagination || null);
    } catch (err) {
      console.error('Error loading subscriptions', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const res = await billingService.listPlans();
      setPlans(Array.isArray(res?.data) ? res.data : []);
    } catch {
      /* ignore */
    }
  };

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);
      const m = await billingService.admin.getMetrics();
      setMetrics(m);
    } catch {
      /* ignore */
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadStripeStatus = async () => {
    try {
      const s = await billingService.admin.stripeStatus();
      setStripeStatus(s);
    } catch {
      /* ignore */
    }
  };

  const handleOpenDetail = async (sub) => {
    try {
      const full = await billingService.admin.getSubscription(sub.id);
      setSelectedSubscription(full);
    } catch (err) {
      AlertService?.error('Error', err?.response?.data?.error?.message || err?.message);
    }
  };

  const handleSyncPlans = async () => {
    const confirmed = await AlertService?.confirm(
      '¿Sincronizar todos los planes con Stripe?',
      'Esto crea o actualiza los Productos y Precios en Stripe a partir del catálogo local.',
    );
    if (!confirmed) return;
    try {
      setSyncingPlans(true);
      const res = await billingService.admin.syncAllPlans();
      AlertService?.success(
        'Sincronización completa',
        `${res?.synced || 0} planes sincronizados, ${res?.errors?.length || 0} errores`,
      );
    } catch (err) {
      AlertService?.error(
        'Error sincronizando',
        err?.response?.data?.error?.message || err?.message,
      );
    } finally {
      setSyncingPlans(false);
    }
  };

  const hayFiltros = Boolean(statusFilter || planFilter || searchDebounced);

  /** Etiqueta del ambiente de Stripe (el id de la cuenta va en el tooltip). */
  const stripeBadge = () => {
    if (!stripeStatus) return null;
    if (!stripeStatus.configured) {
      return (
        <Badge tone="danger" icon="credit_card_off">
          Stripe no configurado en este ambiente
        </Badge>
      );
    }
    return (
      <Badge
        tone={stripeStatus.livemode ? 'ok' : 'warn'}
        icon={stripeStatus.livemode ? 'verified' : 'science'}
        title={stripeStatus.accountId ? `Cuenta de Stripe: ${stripeStatus.accountId}` : undefined}
      >
        Stripe en {stripeStatus.livemode ? 'producción' : 'modo prueba'}
      </Badge>
    );
  };

  /** Celda de empresa (nombre + RFC), compartida por tabla y tarjetas. */
  const Empresa = ({ sub }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell-strong a2-truncate" title={sub.company?.name || '—'}>
        {sub.company?.name || '—'}
      </div>
      <div className="a2-tcell-muted a2-mono a2-truncate">
        {sub.company?.rfc || 'Sin RFC'}
      </div>
    </div>
  );

  /** Plan + marcas de despacho / override manual. */
  const Plan = ({ sub }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell-strong a2-truncate" title={sub.plan?.name || '—'}>
        {sub.plan?.name || '—'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 3 }}>
        {sub.plan?.slug === 'plan-despacho' ? (
          <Badge tone="ok" icon="apartment">Despacho</Badge>
        ) : null}
        {sub.is_manual_override ? (
          <Badge tone="warn" icon="edit_note">Override manual</Badge>
        ) : null}
      </div>
    </div>
  );

  /** Estado + aviso de cancelación programada. */
  const Estado = ({ sub }) => {
    const status = describeStatus(sub.status);
    return (
      <div style={{ minWidth: 0 }}>
        <StatusBadge tone={status.tone} label={status.label} />
        {sub.cancel_at_period_end ? (
          <div className="a2-tcell-muted" style={{ color: 'var(--warn)', marginTop: 4 }}>
            Cancela al terminar el periodo
          </div>
        ) : null}
      </div>
    );
  };

  const empleadosTexto = (sub) => (
    <>
      {sub.employee_count_snapshot ?? '—'}
      {sub.plan?.max_employees != null ? (
        <span className="a2-t-faint"> / {sub.plan.max_employees}</span>
      ) : null}
    </>
  );

  const proximoCobro = (sub) => (
    sub.cancel_at_period_end ? '—' : formatDateLong(sub.current_period_end)
  );

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>Suscripciones - PaySlip</title>
        <meta name="description" content="Gestión central de suscripciones, MRR y cupones de la plataforma" />
      </Helmet>

      {/* Estado de Stripe + acciones de plataforma */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 18,
        }}
      >
        {stripeBadge()}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 'auto' }}>
          <Button
            variant="outline"
            icon="sync"
            onClick={handleSyncPlans}
            loading={syncingPlans}
          >
            {syncingPlans ? 'Sincronizando…' : 'Sincronizar planes con Stripe'}
          </Button>
          <Button icon="sell" onClick={() => navigate('/app/admin/coupons')}>
            Cupones
          </Button>
        </div>
      </div>

      {/* Métricas de facturación */}
      {metricsLoading && !metrics ? (
        <SkeletonKpis count={4} />
      ) : (
        <MetricsCards metrics={metrics} />
      )}

      {/* Reparto por plan */}
      <div style={{ marginTop: 16 }}>
        <PlanBreakdownChart plans={metrics?.plans || []} />
      </div>

      {/* Listado */}
      <Card pad={false} style={{ marginTop: 16 }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, marginBottom: 14,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h3 className="a2-card-title">Suscripciones</h3>
              <p className="a2-card-sub">
                {meta?.total != null
                  ? `${meta.total} ${meta.total === 1 ? 'empresa' : 'empresas'}${hayFiltros ? ' con los filtros actuales' : ' en la plataforma'}`
                  : 'Todas las empresas y su plan'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SearchInput
              value={searchTerm}
              onChange={(e) => { setPage(1); setSearchTerm(e?.target?.value); }}
              placeholder="Buscar por empresa, RFC o correo…"
              aria-label="Buscar suscripción"
              style={{ flex: 1, minWidth: 220, padding: '9px 12px', borderRadius: 9, background: 'var(--surface-2)' }}
            />
            <Select
              value={statusFilter}
              onChange={(e) => { setPage(1); setStatusFilter(e?.target?.value); }}
              aria-label="Filtrar por estado de la suscripción"
              style={{ width: 'auto', minWidth: 180, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {STATUS_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <Select
              value={planFilter}
              onChange={(e) => { setPage(1); setPlanFilter(e?.target?.value); }}
              aria-label="Filtrar por plan"
              style={{ width: 'auto', minWidth: 170, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              <option value="">Todos los planes</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 20 }}>
            <SkeletonRows rows={6} />
          </div>
        ) : subscriptions.length === 0 ? (
          <EmptyState
            icon={hayFiltros ? 'search_off' : 'credit_card'}
            title={hayFiltros ? 'Sin coincidencias' : 'Todavía no hay suscripciones'}
            text={hayFiltros
              ? 'Ajusta la búsqueda o los filtros de estado y plan.'
              : 'En cuanto una empresa contrate un plan aparecerá en esta lista.'}
            className="a2-card"
            style={{ border: 'none' }}
          />
        ) : (
          <>
            {/* Escritorio */}
            <div className="a2-only-lg">
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: GRID_MIN }}>
                  <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                    <div>Empresa</div>
                    <div>Plan</div>
                    <div>Estado</div>
                    <div>Empleados</div>
                    <div>Próximo cobro</div>
                    <div style={{ textAlign: 'right' }}>Precio</div>
                    <div style={{ textAlign: 'right' }}>Detalle</div>
                  </div>

                  {subscriptions.map((sub) => (
                    <div key={sub.id} className="a2-trow" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                      <Empresa sub={sub} />
                      <Plan sub={sub} />
                      <Estado sub={sub} />
                      <div className="a2-tcell">{empleadosTexto(sub)}</div>
                      <div className="a2-tcell">{proximoCobro(sub)}</div>
                      <div className="a2-tcell-strong" style={{ textAlign: 'right' }}>
                        {sub.plan ? formatMxn(sub.plan.price_mxn) : '—'}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                          icon="chevron_right"
                          title={`Ver detalle de ${sub.company?.name || 'la suscripción'}`}
                          aria-label={`Ver detalle de ${sub.company?.name || 'la suscripción'}`}
                          onClick={() => handleOpenDetail(sub)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Móvil: una tarjeta por empresa */}
            <div className="a2-only-sm">
              {subscriptions.map((sub) => (
                <div key={sub.id} style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <Empresa sub={sub} />
                    <IconButton
                      icon="chevron_right"
                      title={`Ver detalle de ${sub.company?.name || 'la suscripción'}`}
                      aria-label={`Ver detalle de ${sub.company?.name || 'la suscripción'}`}
                      onClick={() => handleOpenDetail(sub)}
                    />
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                    <Estado sub={sub} />
                  </div>

                  <div
                    style={{
                      marginTop: 10, display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10,
                    }}
                  >
                    <div>
                      <div className="a2-tcell-muted">Plan</div>
                      <Plan sub={sub} />
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Precio</div>
                      <div className="a2-tcell-strong">
                        {sub.plan ? formatMxn(sub.plan.price_mxn) : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Empleados</div>
                      <div className="a2-tcell">{empleadosTexto(sub)}</div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Próximo cobro</div>
                      <div className="a2-tcell">{proximoCobro(sub)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Paginación */}
        {meta && meta.pageCount > 1 ? (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--line)',
            }}
          >
            <Button
              variant="outline"
              size="sm"
              icon="chevron_left"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="a2-tcell-muted">
              Página {meta.page} de {meta.pageCount} · {meta.total} suscripciones
            </span>
            <Button
              variant="outline"
              size="sm"
              iconRight="chevron_right"
              disabled={page >= meta.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        ) : null}
      </Card>

      {selectedSubscription && (
        <SubscriptionDetailModal
          subscription={selectedSubscription}
          plans={plans}
          onClose={() => setSelectedSubscription(null)}
          onUpdated={(fresh) => {
            setSelectedSubscription(fresh);
            loadSubscriptions();
            loadMetrics();
          }}
        />
      )}
    </div>
  );
};

export default AdminSubscriptions;
